import React, { useState } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { apiFetch } from '../../utils/api';
import {
  Mail,
  Send,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Copy,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface EmailNotificationSectionProps {
  reportId: string;
  claimantName: string;
  claimantEmail?: string;
  healthStatus: 'GREEN' | 'YELLOW' | 'RED';
  missingDocsCount: number;
}

export default function EmailNotificationSection({
  reportId,
  claimantName,
  claimantEmail,
  healthStatus,
  missingDocsCount
}: EmailNotificationSectionProps) {
  const { getToken } = useAuth();
  const [email, setEmail] = useState(claimantEmail || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailHistory, setEmailHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const uploadPortalUrl = `${window.location.origin}/upload/${reportId}`;

  const fetchEmailHistory = async () => {
    try {
      setHistoryLoading(true);
      const token = await getToken();
      const data = await apiFetch<{ emails: any[] }>(
        `/api/v1/notifications/email-history/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailHistory(data.emails || []);
    } catch (err) {
      console.error('Failed to fetch email history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendDocumentRequest = async () => {
    if (!email) return;
    
    try {
      setSending(true);
      setError(null);
      const token = await getToken();
      
      await apiFetch(`/api/v1/notifications/send-document-request/${reportId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          recipient_email: email,
          recipient_name: claimantName
        })
      });
      
      setSent(true);
      setTimeout(() => setDialogOpen(false), 2000);
    } catch (err: any) {
      if (err.message?.includes('429')) {
        setError('Rate limited: An email was sent recently. Wait 24 hours or use force send.');
      } else {
        setError(err.message || 'Failed to send email');
      }
    } finally {
      setSending(false);
    }
  };

  const sendReminder = async (force: boolean = false) => {
    if (!email) return;
    
    try {
      setSending(true);
      setError(null);
      const token = await getToken();
      
      await apiFetch(`/api/v1/notifications/send-reminder/${reportId}?force=${force}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(email)
      });
      
      setSent(true);
      setTimeout(() => setDialogOpen(false), 2000);
    } catch (err: any) {
      if (err.message?.includes('429') && !force) {
        setError('Rate limited. Click "Force Send" to override.');
      } else {
        setError(err.message || 'Failed to send reminder');
      }
    } finally {
      setSending(false);
    }
  };

  const copyUploadLink = () => {
    navigator.clipboard.writeText(uploadPortalUrl);
  };

  // Don't show if health is GREEN (no missing docs)
  if (healthStatus === 'GREEN' && missingDocsCount === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Request Documents</h3>
            <p className="text-sm text-slate-500">
              {missingDocsCount} document{missingDocsCount !== 1 ? 's' : ''} needed from client
            </p>
          </div>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => {
                fetchEmailHistory();
                setDialogOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Email Request
            </Button>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Send Document Request</DialogTitle>
              <DialogDescription>
                Send an email to {claimantName} requesting the missing documents.
              </DialogDescription>
            </DialogHeader>
            
            {sent ? (
              <div className="py-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-900">Email Sent!</p>
                <p className="text-sm text-slate-500">
                  Document request sent to {email}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">
                      Recipient Email
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="client@example.com"
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Upload Portal Link */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">
                      Upload Portal Link
                    </label>
                    <div className="mt-1 flex gap-2">
                      <Input
                        value={uploadPortalUrl}
                        readOnly
                        className="text-xs bg-slate-50"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={copyUploadLink}
                        title="Copy link"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => window.open(uploadPortalUrl, '_blank')}
                        title="Open portal"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  
                  {/* Email History */}
                  {emailHistory.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        Recent Emails ({emailHistory.length})
                      </p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {emailHistory.slice(0, 5).map((email, i) => (
                          <div 
                            key={i}
                            className="flex items-center justify-between text-xs bg-slate-50 rounded p-2"
                          >
                            <div className="flex items-center gap-2">
                              {email.success ? (
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                              ) : (
                                <AlertCircle className="w-3 h-3 text-red-500" />
                              )}
                              <span className="text-slate-600">{email.email_type}</span>
                            </div>
                            <span className="text-slate-400">
                              {new Date(email.sent_at).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <DialogFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendDocumentRequest}
                    disabled={sending || !email}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Request
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Quick Actions */}
      <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
        <button
          onClick={copyUploadLink}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600"
        >
          <Copy className="w-4 h-4" />
          Copy Upload Link
        </button>
        <a
          href={uploadPortalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600"
        >
          <ExternalLink className="w-4 h-4" />
          Preview Portal
        </a>
      </div>
    </div>
  );
}
